import React, { useRef } from 'react';
import styled from 'styled-components';

import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';
import { GradientStart, GradientEnd } from '../buy-it-again-list/buy-it-again-list.styles';

import { OrderRow } from './order-row';

type OrdersTabProps = {
  orders: ConsumerOrder[];
  reorder: (order: ConsumerOrder) => Promise<void>;
  loadingOrderId: string | null;
  handleViewOrder: (order: ConsumerOrder) => void;
};

export function OrdersTab({ orders, reorder, loadingOrderId, handleViewOrder }: OrdersTabProps): JSX.Element {
  const startWatcherRef = useRef<HTMLDivElement>(null);
  const startWatcher = useIntersectionObserver(startWatcherRef);
  const endWatcherRef = useRef<HTMLDivElement>(null);
  const endWatcher = useIntersectionObserver(endWatcherRef);

  return (
    <>
      <GradientStart isVisible={!startWatcher?.isIntersecting} data-testid='orders-gradient-start' />
      <ScrollContainer data-testid='orders-tab'>
        <div ref={startWatcherRef} />
        <Disclaimer>Prices &amp; availability may have changed since last order date.</Disclaimer>
        {orders.map((order) => (
          <OrderRow
            key={order.orderId}
            order={order}
            onReorder={() => void reorder(order)}
            reorderLoading={loadingOrderId === order.orderId}
            onViewOrder={() => handleViewOrder(order)}
          />
        ))}
        <div ref={endWatcherRef} />
      </ScrollContainer>
      <GradientEnd isVisible={!endWatcher?.isIntersecting} data-testid='orders-gradient-end' />
    </>
  );
}

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: none;
  height: 100%;
`;

const Disclaimer = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[40]};
  margin: 0;
  padding: 8px 20px 4px;
`;
